import * as React from 'react';
// import { Tooltip } from 'components';
import { IMeta } from 'models/common';
import { Tooltip } from 'components';
import { translate } from 'react-i18next';
import { useEffect, useState } from 'react';
import renderIcon, { IconName } from 'helpers/renderIcon';
import useDebounce from 'helpers/debounce';
import { addURLSearchParams } from 'helpers/common';
const classNames = require('classnames');

interface IProps {
	input?: any; // dont provide input.value if you want to use debounce
	label?: any;
	type?: any;
	meta?: IMeta;
	autoFocus?: any;
	readOnly?: boolean;
	isDisabled?: boolean;
	onChangeAction?: (e: React.ChangeEvent<HTMLInputElement>) => any;
	tooltipText?: string;
	tooltipPosition?: any;
	description?: string;
	information?: string;
	className?: string;
	customIconName?: IconName;
	customIconClassName?: string;
	customIconAction?: () => void | undefined;
	maxLength?: number;
	customRef?: React.RefObject<HTMLInputElement> | undefined;
	onBlurAction?: () => void | undefined;
	onFocusAction?: () => void | undefined;
	customIconSizeWidth?: number;
	customIconSizeHeight?: number;
	list?: string;
	min?: number;
	highlightWhenValueSet?: boolean;
	updateFieldValueFromOutside?: boolean;
	placeholder?: string;
	step?: number;
	inputMode?: string;
	customCursor?: number;
	/**
	 * For search box for example
	 */
	iconInside?: boolean;

	/**
	 * Debounce props
	 */
	functionToTriggerOnSearch?: (value: string) => void;
	functionToTriggerOnSearchDelay?: number;
	putSelectedValueInUrlQuery?: string; // url prop key name

	errors?: string[];
}

const BookingInputFieldW = (props: IProps) => {
	const {
		onBlurAction,
		onFocusAction,
		highlightWhenValueSet,
		min,
		customIconName,
		customIconClassName,
		putSelectedValueInUrlQuery,
		functionToTriggerOnSearch,
	} = props;
	const [value, setValue] = useState(!props.input?.value ? '' : props.input.value);
	const debouncedValue = useDebounce(
		value,
		props.functionToTriggerOnSearchDelay ? props.functionToTriggerOnSearchDelay : 400,
	);

	//////////////////////////////////////////////
	// Update value on input.value change
	//////////////////////////////////////////////
	useEffect(() => {
		setValue(props.input?.value);
	}, [props.input?.value]);

	//////////////////////////////////////////////
	//////////////////////////////////////////////
	// Update field value from outside when passed input.value prop changes
	//////////////////////////////////////////////
	useEffect(() => {
		if (props.updateFieldValueFromOutside) {
			setValue(props.input?.value);
		}
	}, [props.updateFieldValueFromOutside, props.input?.value]);
	//////////////////////////////////////////////

	// Keep track of caret/cursor position when typing in a field
	useEffect(() => {
		if (props.customRef && props.customRef?.current && props.customCursor) {
			props.customRef.current.setSelectionRange(props.customCursor, props.customCursor);
		}
	}, [props.customRef, props.customCursor, props.input?.value]);

	//////////////////////////////////////////////
	// Debounce function before triggering it
	//////////////////////////////////////////////
	useEffect(() => {
		if (functionToTriggerOnSearch) {
			if (debouncedValue?.length && putSelectedValueInUrlQuery) {
				addURLSearchParams(putSelectedValueInUrlQuery, debouncedValue, history);
			}
			functionToTriggerOnSearch(debouncedValue);
		}
	}, [debouncedValue]);

	let informationContainer: JSX.Element = null;
	if (typeof props.information !== 'undefined' && props.information !== null) {
		informationContainer = <div className="informationBlock">{props.information}</div>;
	}

	let description: string | string[] = props.description || '';
	let status = '';
	if (props.meta?.touched) {
		if (props.meta?.warning) {
			status = 'withWarning';
			description = props.meta.warning;
		}
		if (props.meta?.error) {
			description = props.meta.error;
			status = 'withError';
		}
	}

	if (props.maxLength) {
		const maxLength = Number(props.maxLength);
		const value = String(props.input?.value || '');
		if (value.length > maxLength) {
			description = translate('Validation_Field_TooLong');
			status = 'withError';
		} else if (value.length <= maxLength) {
			description = [description as string, value.length + '/' + maxLength];
		}
	}

	return (
		<div
			className={classNames('bookingFieldGroup', status, props.className, {
				readOnly: props.readOnly,
				disabled: props.isDisabled,
				iconInside: props.iconInside,
			})}
		>
			{informationContainer}
			<div className={`inputHolder ${highlightWhenValueSet && props.input?.value ? 'highlight' : ''}`}>
				{customIconName ? renderIcon(customIconName, 16, 16, customIconClassName) : null}
				<input
					{...props.input}
					className={classNames('inputField', {
						value: props.input?.value,
						withoutLabel: !props.label,
					})}
					type={props.type}
					autoFocus={props.autoFocus}
					readOnly={props.readOnly}
					disabled={props.isDisabled}
					ref={props.customRef}
					list={props.list}
					value={value}
					min={min}
					step={props.step}
					inputMode={props.inputMode}
					onChange={(e) => {
						if (props.onChangeAction) {
							props.onChangeAction(e);
						}
						if (props.input?.onChange) {
							props.input.onChange(e);
						}
						setValue(e.target.value);
					}}
					onBlur={onBlurAction}
					onFocus={onFocusAction}
					placeholder={props?.placeholder}
				/>
				{props.tooltipText && props.tooltipText.length > 0 ? (
					<Tooltip position={props.tooltipPosition} message={props.tooltipText}>
						<a href="#" onClick={(e) => e.preventDefault()}>
							<img src={require('containers/Html/icons/tooltip-icon.png')} />
						</a>
					</Tooltip>
				) : null}
			</div>
			<div className="inputFieldErrors">
				{props.errors ? (
					props.errors.map((err, index) => (
						<span key={index} className="note">
							{err}
						</span>
					))
				) : description ? (
					<span className={'note' + (Array.isArray(description) ? ' split' : '')}>
						{Array.isArray(description) ? description.map((d, i) => <span key={i}>{d}</span>) : description}
					</span>
				) : null}
			</div>
		</div>
	);
};

export const BookingInputField = BookingInputFieldW;
